// SPDX-FileCopyrightText: 2023-2025 Open Pioneer project (https://github.com/open-pioneer)
// SPDX-License-Identifier: Apache-2.0

import { batch, computed, reactive, ReadonlyReactive, watch } from "@conterra/reactivity-core";
import {
    createLogger,
    deprecated,
    destroyResource,
    isAbortError,
    Resource
} from "@open-pioneer/core";
import { ImageWrapper } from "ol";
import WMSCapabilities from "ol/format/WMSCapabilities";
import ImageLayer from "ol/layer/Image";
import type ImageSource from "ol/source/Image";
import type { Options as WMSSourceOptions } from "ol/source/ImageWMS";
import ImageWMS from "ol/source/ImageWMS";
import { sourceId } from "open-pioneer:source-info";
// oxlint-disable-next-line @typescript-eslint/no-unused-vars
import type { LayerFactory } from "../LayerFactory";
import { MapModel } from "../model/MapModel";
import { fetchText } from "../utils/fetch";
import { INTERNAL_CONSTRUCTOR_TAG, InternalConstructorTag } from "../utils/InternalConstructorTag";
import { AbstractLayer } from "./AbstractLayer";
import {
    ATTACH_TO_MAP,
    ATTACH_TO_PARENT,
    DETACH_FROM_MAP,
    GET_DEPS,
    GET_RAW_SUBLAYERS,
    LayerConstructor,
    LayerDependencies,
    SET_LEGEND,
    SET_METADATA_LOAD_INFO
} from "./shared/internals";
import { LayerConfig } from "./shared/LayerConfig";
import { SublayersCollection } from "./shared/SublayersCollection";
import { Sublayer } from "./unions";
import { getAttributions } from "./wms/getAttributions";
import { getLegendUrl } from "./wms/getLegendUrl";
import {
    constructSublayers,
    SET_SUBLAYER_LOAD_INFO,
    WMSSublayer,
    WMSSublayerConfig
} from "./wms/WMSSublayer";

/**
 * Configuration options to construct a {@link WMSLayer}.
 *
 * @group Layers
 */
export interface WMSLayerConfig extends LayerConfig {
    /** URL of the WMS service. */
    url: string;

    /** Configures the layer's sublayers. */
    sublayers?: WMSSublayerConfig[];

    /**
     * Additional source options for the layer's WMS source.
     *
     * NOTE: These options are intended for advanced configuration:
     * the WMS Layer manages some of the OpenLayers source options itself.
     */
    sourceOptions?: Partial<WMSSourceOptions>;

    /**
     * Whether to automatically fetch capabilities from the service when needed (default: `true`).
     *
     * Setting this to `false` can be useful as a performance optimization when capabilities are not really required by the application.
     * Note that this will disable some features of the WMS layer: for example, the legend URL will not be available.
     */
    fetchCapabilities?: boolean;
}

const LOG = createLogger(sourceId);

const deprecatedConstructor = deprecated({
    name: "WMSLayer constructor",
    packageName: "@open-pioneer/map",
    since: "v1.0.0",
    alternative: "use LayerFactory.create() instead"
});

/**
 * Displays an OGC Web Map Service (WMS).
 *
 * @group Layers
 */
export class WMSLayer extends AbstractLayer {
    #url: string;
    #sublayers: SublayersCollection<WMSSublayer>;
    #layer: ImageLayer<ImageSource>;
    #source: ImageWMS;
    #fetchCapabilities: boolean;

    #loadStarted = false;
    // oxlint-disable-next-line @typescript-eslint/no-explicit-any
    #capabilities: Record<string, any> | undefined;
    readonly #invalidSublayerNames = reactive<Set<string>>();
    readonly #abortController = new AbortController();

    #visibleSublayers: ReadonlyReactive<string[]>;
    #sublayersWatch: Resource | undefined;

    /**
     * @deprecated Prefer using {@link LayerFactory.create} instead of calling the constructor directly
     */
    constructor(config: WMSLayerConfig);

    /**
     * NOTE: Do not use this overload. Use {@link LayerFactory.create} instead.
     *
     * @internal
     */
    constructor(
        config: WMSLayerConfig,
        deps: LayerDependencies,
        internalTag: InternalConstructorTag
    );
    constructor(
        config: WMSLayerConfig,
        deps?: LayerDependencies,
        internalTag?: InternalConstructorTag
    ) {
        if (!internalTag) {
            deprecatedConstructor();
        }

        const layer = new ImageLayer();
        super(
            {
                ...config,
                olLayer: layer
            },
            deps,
            internalTag
        );
        const source = new ImageWMS({
            ...config.sourceOptions,
            url: config.url,
            params: {
                ...config.sourceOptions?.params
            },
            // Use http service to load tiles; needed for authentication etc.
            imageLoadFunction: (wrapper, url) => {
                return this.#loadImage(wrapper, url).catch((error) => {
                    LOG.error(`Failed to load tile at '${url}'`, error);
                });
            }
        });
        this.#url = config.url;
        this.#fetchCapabilities = config.fetchCapabilities ?? true;
        this.#source = source;
        this.#layer = layer;

        this.#sublayers = new SublayersCollection(
            constructSublayers(config.sublayers),
            INTERNAL_CONSTRUCTOR_TAG
        );
        this.#sublayers
            [GET_RAW_SUBLAYERS]()
            .forEach((sublayer) => sublayer[ATTACH_TO_PARENT](this, this));

        this.#visibleSublayers = computed(() => this.#getVisibleLayerNames(), {
            equal(a, b) {
                return a.length === b.length && a.every((v, i) => v === b[i]);
            }
        });

        this.#sublayersWatch = watch(
            () => [this.#visibleSublayers.value],
            ([layers]) => {
                this.#updateLayersParam(layers);
            },
            {
                immediate: true
            }
        );
    }

    override destroy() {
        this.#abortController.abort();
        this.#sublayersWatch = destroyResource(this.#sublayersWatch);
        super.destroy();
    }

    get type() {
        return "wms" as const;
    }

    get legend() {
        return undefined;
    }

    /**
     * Returns the current source associated with this layer as a reactive property.
     *
     * Note that the return type may be expanded in the future with additional source types.
     */
    get olSource(): ImageWMS | undefined {
        // source does not change in this class; no signal is required
        return this.#source;
    }

    /** The URL of the WMS service that was used during layer construction. */
    get url(): string {
        return this.#url;
    }

    get layers(): undefined {
        return undefined;
    }

    /**
     * Holds the sublayers of this layer.
     */
    get sublayers(): SublayersCollection<WMSSublayer> {
        return this.#sublayers;
    }

    /**
     * @internal
     *
     * Note: this property was never documented and has been exposed by accident.
     **/
    get capabilities() {
        return this.#capabilities;
    }

    /** @internal */
    [ATTACH_TO_MAP](map: MapModel): void {
        super[ATTACH_TO_MAP](map);
        for (const sublayer of this.#sublayers.getSublayers()) {
            sublayer[ATTACH_TO_MAP](map);
        }

        this.#load();
    }

    /** @internal */
    [DETACH_FROM_MAP](): void {
        super[DETACH_FROM_MAP]();
        for (const sublayer of this.#sublayers.getSublayers()) {
            sublayer[DETACH_FROM_MAP]();
        }
    }

    #load() {
        if (this.#loadStarted || !this.#fetchCapabilities) {
            return;
        }
        this.#loadStarted = true;
        this[SET_METADATA_LOAD_INFO]("loading");
        this.#fetchWMSCapabilities()
            .then((result: string) => {
                batch(() => {
                    this.#initializeWithMetadata(result);
                    this[SET_METADATA_LOAD_INFO]("loaded");
                });
            })
            .catch((error: unknown) => {
                if (isAbortError(error)) {
                    LOG.debug(`Layer '${this.id}' has been destroyed before fetching capabilities`);
                }
                LOG.error(`Failed to initialize WMS layer '${this.id}'`, error);
                const wrappedError =
                    error instanceof Error
                        ? error
                        : new Error(`Failed to initialize WMS layer '${this.id}'`);
                this[SET_METADATA_LOAD_INFO]({
                    kind: "error",
                    error: wrappedError
                });
            });
    }

    /**
     * Gathers the visibility of _all_ sublayers and assembles the 'layers' WMS parameter.
     * The parameters are then applied to the WMS source.
     */
    #updateLayersParam(layers: string[]) {
        this.#source.updateParams({
            LAYERS: layers
        });

        // only set source if there are visible sublayers, otherwise
        // we send an invalid http request
        const source = layers.length === 0 ? null : this.#source;
        if (this.#layer.getSource() !== source) {
            this.#layer.setSource(source);
        }
    }

    #getVisibleLayerNames() {
        const invalidSublayerNames = this.#invalidSublayerNames.value;

        const layers: string[] = [];
        const filter = (sublayer: Sublayer) => sublayer.visible;
        for (const sublayer of walkLeaves(this.#sublayers, filter)) {
            // Skip sublayers with an invalid name: including them in the request would
            // make the whole GetMap fail and hide the valid sublayers as well.
            if (sublayer.name && invalidSublayerNames && invalidSublayerNames.has(sublayer.name)) {
                continue;
            }
            // Push sublayer if layer name is not an empty string | undefined | ...
            if (sublayer.name) {
                layers.push(sublayer.name);
            }
        }
        return layers;
    }

    async #fetchWMSCapabilities(): Promise<string> {
        const httpService = this[GET_DEPS]().httpService;
        const url = `${this.#url}?LANGUAGE=ger&SERVICE=WMS&REQUEST=GetCapabilities`;
        return fetchText(url, httpService, this.#abortController.signal);
    }

    #initializeWithMetadata(metadata: string) {
        const parser = new WMSCapabilities();
        const capabilities = parser.read(metadata);
        this.#capabilities = capabilities;

        // Apply attributions from metadata if none are set.
        if (this.#source.getAttributions() == null) {
            const attributions = getAttributions(capabilities);
            if (attributions) {
                this.#source.setAttributions(attributions);
            }
        }

        const hasCapabilityTree = !!capabilities?.Capability?.Layer;
        const sublayerNames = hasCapabilityTree ? collectLayerNames(capabilities) : undefined;
        const invalidSublayerNames = new Set<string>();
        for (const sublayer of walkLeaves(this.#sublayers)) {
            if (!sublayer.name) {
                continue;
            }
            if (sublayerNames && !sublayerNames.has(sublayer.name)) {
                LOG.error(
                    `WMS sublayer name '${sublayer.name}' of layer '${this.id}' not found in capabilities.`
                );
                invalidSublayerNames.add(sublayer.name);
                sublayer[SET_SUBLAYER_LOAD_INFO]({
                    kind: "error",
                    error: new Error(
                        `WMS sublayer name '${sublayer.name}' not found in capabilities.`
                    )
                });
                continue;
            }
            sublayer[SET_SUBLAYER_LOAD_INFO]("loaded");
            const legendUrl = getLegendUrl(capabilities, sublayer.name);
            sublayer[SET_LEGEND](legendUrl);
        }

        this.#invalidSublayerNames.value = invalidSublayerNames;
    }

    async #loadImage(imageWrapper: ImageWrapper, imageUrl: string): Promise<void> {
        const httpService = this[GET_DEPS]().httpService;
        const image = imageWrapper.getImage() as HTMLImageElement;

        const response = await httpService.fetch(imageUrl);
        if (!response.ok) {
            throw new Error(`Request failed with status ${response.status}.`);
        }

        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const finish = () => {
            // Cleanup object URL after load to prevent memory leaks.
            // https://stackoverflow.com/questions/62473876/openlayers-6-settileloadfunction-documented-example-uses-url-createobjecturld
            URL.revokeObjectURL(objectUrl);
            image.removeEventListener("load", finish);
            image.removeEventListener("error", finish);
        };

        image.addEventListener("load", finish);
        image.addEventListener("error", finish);
        image.src = objectUrl;
    }
}

/**
 * Collects all layer `Name` values from a parsed WMS capabilities document.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function collectLayerNames(capabilities: Record<string, any> | undefined): Set<string> {
    const names = new Set<string>();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const visit = (layer: Record<string, any> | undefined) => {
        if (!layer) {
            return;
        }
        if (typeof layer.Name === "string") {
            names.add(layer.Name);
        }
        if (Array.isArray(layer.Layer)) {
            for (const child of layer.Layer) {
                visit(child);
            }
        }
    };
    visit(capabilities?.Capability?.Layer);
    return names;
}

/**
 * Yields all leaf notes in the given collection (recursively).
 */
function* walkLeaves(
    sublayers: SublayersCollection<WMSSublayer>,
    filter?: (sublayer: Sublayer) => boolean
): Generator<WMSSublayer> {
    for (const sublayer of sublayers[GET_RAW_SUBLAYERS]()) {
        if (filter && !filter(sublayer)) {
            continue;
        }

        const nested = sublayer.sublayers[GET_RAW_SUBLAYERS]();
        if (nested.length) {
            yield* walkLeaves(sublayer.sublayers, filter);
        } else {
            yield sublayer;
        }
    }
}

// Ensure layer class is assignable to the constructor interface (there is no "implements" for the class itself).
// oxlint-disable-next-line no-constant-condition
if (false) {
    const check: LayerConstructor<WMSLayerConfig, WMSLayer> = WMSLayer;
    void check;
}
